import * as models from 'smg/admin/models';
import { HasPerm, Session } from 'smg/admin/session';
import * as toast from 'smg/toast';
import { DeleteCart, GetCart, RestoreCart } from '../cart/cart';
import * as cartID from '../cart/cartid';
import { AddCartItems } from '../cart/cartitems';
import * as carts from '../cart/cartactions';
import * as accounts from '../accounts';
import { Relref } from '../hugo';
import * as column from '../table/column';
import * as list from '../list/page';
import { Table as DataTable } from '../table/table';

const sess = Session();
const viewCartBtn = document.querySelector('#view-cart-button') as HTMLButtonElement;
const newCartBtn = document.querySelector('#new-cart-button') as HTMLButtonElement;
const shareCartBtn = document.querySelector('#share-button') as HTMLButtonElement;

declare interface myWindow extends Window {
    readonly IAPV2Root: string;
}
declare const window: myWindow;

type R = models.HyejooCart;
type C = keyof R;
type D = never;

class Table extends list.ListPage<R, DataTable<R, C, D>> {
    rowsPerPage = 50;

    constructor() {
        super('/carts', new DataTable<R, C, D>({}), '/carts/filters');

        this.loader.getRowsFromResponse = (response: unknown) => models.CartsResponse.fromJSON(response).carts;
        this.loader.drawRow = (tr: HTMLTableRowElement, r: models.HyejooCart) => {
            tr.removeAttribute('class');

            if (!r.activeOrOrdered) {
                tr.classList.add('table-disabled');
            } else if (r.ordered) {
                tr.classList.add('table-success');
            }
        };

        this.loader.alwaysOnColumns = ['id', 'activeOrOrdered', 'active', 'ordered'];
        this.loader.addColumns(
            new column.ColumnID({
                visible: false,
            }),
            new column.ColumnID({
                name: 'ParentCartID',
                width: 10,
                visible: false,
                columns: ['parentCartID'],
                draw: (td, row) => {
                    const a = document.createElement('a');
                    a.textContent = row.parentCartID || '';
                    a.classList.add('text-reset');

                    td.appendChild(a);
                    a.href = `${Relref('/carts')}?q=id:${row.parentCartID ?? ''}+user:all`;
                },
            }),
            new column.ColumnUser({
                name: 'Customer',
                column: 'user',
                columns: ['user', 'companyID'],
                width: 15,
                minWidth: '10rem',
                user: r => r.user?.id !== undefined ? r.user : undefined,
                displayName: r => r.user?.fullNameWithCompany() || 'View Customer',
                href: r => r.user?.iapURL(),
            }),
            new column.ColumnID({
                name: 'PageRequestID',
                columns: ['pageRequestID'],
                visible: false,
                draw: (td, row) => {
                    if (!row.pageRequestID) {
                        return;
                    }
                    const a = document.createElement('a');
                    a.textContent = row.pageRequestID || '';
                    a.classList.add('text-reset');

                    td.appendChild(a);
                    a.href = `${Relref('/page-requests')}?id=${row.pageRequestID ?? ''}`;
                },
            }),
            new column.Column({
                name: 'Name',
                columns: ['name'],
                width: 10,
                overflowHidden: true,
                text: row => row.name || '',
            }),
            new column.ColumnNumber({
                name: 'Summary',
                column: 'cartItems',
                width: 10,
                tooltip: true,
                draw: async (td, row) => {
                    td.textContent = await carts.SummarizeCart(row);
                    if (td.textContent === 'empty cart') {
                        td.classList.add('text-muted', 'fst-italic');
                    }
                },
            }),
            new column.ColumnDollar({
                name: 'Total',
                column: 'cartItems',
                width: '6rem',
                disableSort: true,
                showZero: true,
                number: row => carts.CartTotal(row),
            }),
            new column.ColumnCheckbox({
                name: 'Admin',
                column: 'admin',
                width: 4,
                editable: false,
            }),
            new column.Column({
                name: 'Orders',
                columns: 'cartOrders',
                width: '6rem',
                overflowHidden: false,
                draw: (td, row) => {
                    if (!row.cartOrders?.length) {
                        return;
                    }

                    const invoiceNumbers: number[] = [];

                    for (const cartOrder of row.cartOrders) {
                        if (!cartOrder?.orders) {
                            continue;
                        }
                        for (const order of cartOrder.orders) {
                            if (order?.invoiceNumber) {
                                invoiceNumbers.push(order.invoiceNumber);
                            }
                        }
                    }
                    switch (invoiceNumbers.length) {
                    case 0:
                        td.textContent = '';
                        break;
                    case 1: {
                        const a = document.createElement('a');
                        a.textContent = invoiceNumbers[0].toString();
                        a.href = `${window.IAPV2Root}/orders/${invoiceNumbers[0]}`;
                        a.classList.add('text-reset');
                        td.appendChild(a);
                        break;
                    }
                    default: {
                        const $parent = document.createElement('div');
                        $parent.classList.add('dropdown-center');
                        td.appendChild($parent);

                        const $dropdownLink = document.createElement('a');
                        $dropdownLink.href = '#';
                        $dropdownLink.classList.add('dropdown-toggle', 'text-reset');
                        $dropdownLink.setAttribute('type', 'button');
                        $dropdownLink.setAttribute('data-bs-toggle', 'dropdown');
                        $dropdownLink.setAttribute('aria-expanded', 'false');
                        $dropdownLink.textContent = `${invoiceNumbers.length} Orders`;
                        $parent.appendChild($dropdownLink);

                        const $dropdownMenu = document.createElement('ul');
                        $dropdownMenu.classList.add('dropdown-menu', 'text-center');
                        $dropdownMenu.style.width = '5rem';
                        $dropdownMenu.style.minWidth = '0';
                        $dropdownMenu.setAttribute('aria-labelledby', 'dropdownMenuButton');
                        $parent.appendChild($dropdownMenu);

                        for (const invoiceNumber of invoiceNumbers) {
                            const li = document.createElement('li');
                            const a = document.createElement('a');
                            a.textContent = invoiceNumber.toString();
                            a.href = `${window.IAPV2Root}/orders/${invoiceNumber}`;
                            a.classList.add('text-reset');
                            li.appendChild(a);
                            $dropdownMenu.appendChild(li);
                        }
                        td.appendChild($parent);
                    }
                    }
                },
            }),
            new column.ColumnRealm(),
            new column.ColumnDate({
                name: 'Created',
                columns: ['created'],
                sort: { column: 'created', direction: 'desc' },
            }),
            new column.ColumnActions({
                columns: ['active'],
                draw: async (dropdown, r) => {
                    {
                        const sess = await Session();
                        const li = createColumnLink('Set as Cart', () => {
                            const oldCartID = cartID.Get();
                            const newCartID = r.id || '';
                            if (r.user?.id === sess.user.id && newCartID) {
                                void carts.SwitchCart(newCartID, oldCartID);
                            } else {
                                void newCartFromCart(oldCartID, newCartID);
                            }
                        }, ['fas', 'fa-shopping-cart', 'pe-1']);

                        dropdown.appendChild(li);
                    }

                    {
                        const li = createColumnLink('Add to Cart', async () => {
                            if (r.id) {
                                await addCartToCart(r);
                            }
                        }, ['fas', 'fa-cart-plus', 'pe-1']);
                        dropdown.appendChild(li);
                    }

                    {
                        let li: HTMLElement;
                        if (r.active) {
                            li = createColumnLink('Delete', async () => {
                                if (r.id) {
                                    await deleteCartFromTable(r.id || '');
                                    void t.send();
                                }
                            }, ['fas', 'fa-trash', 'pe-1']);
                            dropdown.appendChild(li);
                        } else {
                            li = createColumnLink('Restore', async () => {
                                if (r.id) {
                                    await restoreCartFromTable(r.id || '');
                                    void t.send();
                                }
                            }, ['fas', 'fa-trash-restore', 'pe-1']);
                            dropdown.appendChild(li);
                        }
                    }
                },
            }));
        this.enableAutoUpdating(60 * 1000);
    }
}

const t = new Table();

const urlParams = new URLSearchParams(window.location.search);
t.preSend = params => {
    if (urlParams.has('withoutPricing')) {
        t.loader.hideColumn('Total');
    } else {
        t.loader.showColumn('Total');
        params['withPricing'] = 'true';
    }
};

const createColumnLink = (linkText: string, clickListener: () => void | Promise<void>, iconClasses?: string[]): HTMLElement => {
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.classList.add('dropdown-item');
    a.textContent = linkText;
    a.href = '#';
    li.appendChild(a);

    const i = document.createElement('i');
    if (iconClasses) {
        for (const className of iconClasses) {
            i.classList.add(className);
        }
    }

    a.insertBefore(i, a.firstChild);
    a.addEventListener('click', async e => {
        e.preventDefault();
        await clickListener();
    });
    return li;
};

async function deleteCartFromTable(cartID: string) {
    await carts.DeleteCartWithToast(cartID).then(() => void t.send());
}

async function restoreCartFromTable(cartID: string) {
    await carts.RestoreCartWithToast(cartID).then(() => void t.send());
}

// Create a new cart fron a parent cart. Users can't set a cart as active unless they own it.
async function newCartFromCart(previousCartID: string, parentCartID: string) {
    await AddCartItems({ cartID: parentCartID }, true).then(c => {
        void t.send();
        const newCartID = c.cartID;
        // Delete the new cart and switch back to the original cart. Pass the parentCartID
        // for the redo function.
        toast.Undo('Created duplicate cart.', () => undoNewCartFromCart(newCartID, previousCartID));
    });
}

// Deletes childCart, switches to previousCart.
async function undoNewCartFromCart(childCartID: string, previousCartID: string) {
    await carts.SwitchCart(previousCartID, undefined, false).then(async () => {
        await DeleteCart(childCartID).then(() => {
            void t.send();
            toast.Undo('Deleted duplicate cart.', () => redoNewCartFromCart(childCartID, previousCartID));
        });
    });
}

// Restores and switches to childCart. Passes the previousCartID for the undo function.
async function redoNewCartFromCart(childCartID: string, previousCartID: string) {
    await RestoreCart(childCartID).then(async () => {
        await carts.SwitchCart(childCartID, undefined, false).then(() => {
            void t.send();
            toast.Undo('Restored duplicate cart.', () => undoNewCartFromCart(childCartID, previousCartID));
        });
    });
}

async function addCartToCart(parentCart: models.HyejooCart) {
    const currentCart = await GetCart().then(resp => resp?.cart);
    let newCart = false;
    if (!currentCart?.id || currentCart.id === '') {
        newCart = true;
    }
    if (!parentCart.id || parentCart.id === '' || !parentCart.cartItems || parentCart.cartItems.length === 0) {
        return;
    }
    await AddCartItems({ cartID: parentCart.id }, newCart).then(cartResp => {
        if (!cartResp.cartID || cartResp.cartID === '' || cartResp.cartItems?.length === 0) {
            return;
        }
        let newCartItems: models.CartItem[] = cartResp.cartItems;
        if (currentCart?.cartItems) {
            newCartItems = cartResp.cartItems.filter(v => !currentCart.cartItems?.find(cartItem => v.id === cartItem.id));
        }
        void t.send();
        toast.Undo('Added items to current cart.', () => carts.UndoAddItemsToCart({
            cartID: cartResp.cartID,
            cartItems: newCartItems,
        }));
    });
}

viewCartBtn.addEventListener('click', e => {
    e.preventDefault();
    window.location.href = accounts.WithProfile(Relref('/cart'));
});

newCartBtn.addEventListener('click', async e => {
    e.preventDefault();
    await cartID.Forget().then(() => {
        window.location.href = accounts.WithProfile(Relref('/cart'));
    });
});

shareCartBtn.addEventListener('click', e => {
    e.preventDefault();
    void carts.ShareCart(cartID.Get());
});

async function main() {
    if (!await HasPerm('r/carts')) {
        window.location.replace(Relref('/'));
        return;
    }

    let defaultQuery = 'activeOrOrdered:yes';
    if (!(await sess).user.userAdminRoles?.find(r => r.adminRole?.name === 'Developer')) {
        defaultQuery += 'user:me';
    }
    t.defaultQuery = defaultQuery;
    t.defaultSortString = 'created desc';

    await t.build().then(() => {
        if (urlParams.has('viewCart')) {
            viewCartBtn.click();
        }
    });
}

void main();
